import SameTabLink from '@/components/common/SameTabLink';
import { useState } from 'react';
import {
  ChevronDown,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
} from 'lucide-react';
import { footerColumns, socialLinks } from '@/config/navigation';
import { siteConfig } from '@/config/site';
import { legalRoutes } from '@/config/legal';
import type { FooterColumn, SocialLink } from '@/types/navigation';

const socialIcons = {
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  facebook: Facebook,
} as const;

function SocialIconButton({ social }: { social: SocialLink }) {
  const Icon = socialIcons[social.icon];
  const isExternal = social.href.startsWith('http');

  return (
    <a
      href={social.href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className="group flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-gray-700 bg-gray-900/80 text-gray-300 transition-all duration-300 hover:border-orange-500 hover:bg-orange-500 hover:text-white hover:scale-105"
      aria-label={social.label}
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}

function FooterLinkColumn({ column }: { column: FooterColumn }) {
  return (
    <div className="space-y-4">
      {!column.hideTitle && (
        <h3
          className={`text-xs font-bold uppercase tracking-wider ${
            column.isSpecial ? 'text-orange-500' : 'text-white'
          }`}
        >
          {column.title}
          {column.isSpecial && (
            <span className="ml-2 rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold text-black normal-case tracking-normal">
              new
            </span>
          )}
        </h3>
      )}
      {column.hideTitle && <div className="hidden lg:block h-5" aria-hidden="true" />}
      <ul className="space-y-2.5">
        {column.links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              className="group inline-flex text-sm text-gray-400 transition-colors duration-200 hover:text-orange-400"
            >
              <span className="border-b border-transparent group-hover:border-orange-400/40 transition-colors">
                {link.label}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MobileAccordion({ column }: { column: FooterColumn }) {
  const [isOpen, setIsOpen] = useState(false);

  if (column.hideTitle) return null;

  return (
    <div className="border-b border-gray-800 lg:hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-3 text-left"
        aria-expanded={isOpen}
      >
        <span
          className={`text-xs font-bold uppercase tracking-wider ${
            column.isSpecial ? 'text-orange-500' : 'text-white'
          }`}
        >
          {column.title}
          {column.isSpecial && (
            <span className="ml-2 rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold text-black normal-case">
              new
            </span>
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <ul className="space-y-2 pb-3">
          {column.links.map((link) => (
            <li key={link.label}>
              <a href={link.href} className="text-sm text-gray-400 hover:text-orange-400 transition-colors">
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function getMobileColumns(): FooterColumn[] {
  const solutionLinks = footerColumns
    .filter((col) => col.id.startsWith('solutions'))
    .flatMap((col) => col.links);

  const otherColumns = footerColumns.filter(
    (col) => !col.hideTitle && !col.id.startsWith('solutions'),
  );

  const whyColumn = otherColumns.find((col) => col.id === 'why');
  const remainingColumns = otherColumns.filter((col) => col.id !== 'why');

  return [
    ...(whyColumn ? [whyColumn] : []),
    { id: 'solutions', title: 'SOLUTIONS', links: solutionLinks },
    ...remainingColumns,
  ];
}

export default function Footer() {
  const mobileColumns = getMobileColumns();

  return (
    <footer id="footer" className="relative bg-black border-t border-gray-800">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />

      <div className="page-container pt-10 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-8">
        <div className="grid grid-cols-1 gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-8 mb-8 lg:mb-12">
          {/* Brand column */}
          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            <a href="/" className="inline-block">
              <img
                src={siteConfig.logo.white}
                alt={`${siteConfig.name} Logo`}
                className="h-9 w-auto sm:h-11"
              />
            </a>
            <p className="text-sm leading-relaxed text-gray-400 max-w-xs">
              {siteConfig.tagline}
            </p>
            <div className="flex items-center gap-2.5 sm:gap-3">
              {socialLinks.map((social) => (
                <SocialIconButton key={social.label} social={social} />
              ))}
            </div>
          </div>

          {/* Desktop link columns */}
          <div className="hidden lg:grid lg:col-span-9 lg:grid-cols-7 lg:gap-4 xl:gap-6">
            {footerColumns.map((column) => (
              <FooterLinkColumn key={column.id} column={column} />
            ))}
          </div>

          {/* Mobile accordion */}
          <div className="lg:hidden">
            {mobileColumns.map((column) => (
              <MobileAccordion key={column.id} column={column} />
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-800 pt-5 sm:pt-6 sm:flex-row sm:gap-4">
          <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
            Copyright © {new Date().getFullYear()} {siteConfig.copyright}
          </p>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
            <SameTabLink to={legalRoutes.termsConditions} className="transition-colors hover:text-orange-400">
              Terms &amp; Conditions
            </SameTabLink>
            <span className="text-gray-600">|</span>
            <SameTabLink to={legalRoutes.privacyPolicy} className="transition-colors hover:text-orange-400">
              Privacy Policy
            </SameTabLink>
          </div>
        </div>
      </div>
    </footer>
  );
}
